import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import SEO from "@/components/SEO";

export default function Cities() {
  const [data, setData] = useState({ total: 0, provinces: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openProvince, setOpenProvince] = useState(null);

  useEffect(() => {
    api.get("/cities-with-counts")
      .then((r) => {
        setData(r.data);
        // auto-open the busiest province
        if (r.data.provinces?.[0]) setOpenProvince(r.data.provinces[0].slug);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return data.provinces;
    const q = query.trim().toLowerCase();
    return data.provinces
      .map((p) => {
        const cities = p.cities.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            (c.towns || []).some((t) => t.name.toLowerCase().includes(q))
        );
        return { ...p, cities };
      })
      .filter((p) => p.cities.length > 0);
  }, [data, query]);

  return (
    <div className="min-h-screen pt-28 md:pt-32" data-testid="cities-page">
      <SEO
        title="Companions by City"
        description={`Browse ${data.total || "hundreds of"} verified companions across every South African city — Johannesburg, Cape Town, Durban, Pretoria and every town in between.`}
      />
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        {/* Hero */}
        <div className="mb-14">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">
              South Africa &middot; {data.total} live listings
            </p>
            <h1 className="font-heading text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl md:text-8xl">
              Every city.<br />
              <span className="italic text-velvet-rose">Every desire.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/60">
              Browse companions by province, city, and neighbourhood. Numbers are live —
              tap a location to open the directory pre-filtered to that spot.
            </p>
          </Reveal>

          {/* Search */}
          <div className="mt-10 flex max-w-md items-center gap-3 border-b border-white/15 pb-3">
            <Search size={16} className="text-white/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a city or neighbourhood…"
              data-testid="cities-search"
              className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
            />
          </div>
        </div>

        {/* Provinces */}
        {loading ? (
          <div className="py-20 text-center text-sm font-light text-white/40">Loading locations…</div>
        ) : (
          <div className="space-y-4">
            {filtered.map((p) => (
              <div
                key={p.slug}
                data-testid={`cities-province-${p.slug}`}
                className="border border-white/10 bg-velvet-coal/40 backdrop-blur-sm"
              >
                <button
                  onClick={() => setOpenProvince(openProvince === p.slug ? null : p.slug)}
                  className="flex w-full items-center justify-between px-6 py-6 text-left transition-colors hover:bg-white/[0.03] md:px-8"
                  data-testid={`cities-province-toggle-${p.slug}`}
                >
                  <div>
                    <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">Province</p>
                    <h2 className="mt-1 font-heading text-3xl font-light tracking-tight text-white md:text-4xl">
                      {p.name}
                    </h2>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="text-sm text-white/60">
                      {p.count} listing{p.count === 1 ? "" : "s"}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-white/40 transition-transform duration-500 ${openProvince === p.slug ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {openProvince === p.slug && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 gap-px bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
                        {p.cities.map((c) => (
                          <div key={c.slug} className="bg-velvet-ink p-6">
                            <Link
                              to={`/directory?city=${c.slug}`}
                              data-testid={`cities-city-link-${c.slug}`}
                              className="group flex items-baseline justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <MapPin size={14} className="text-velvet-gold" />
                                <span className="text-lg font-light text-white transition-colors group-hover:text-velvet-rose">
                                  {c.name}
                                </span>
                              </div>
                              <span
                                className={`text-sm tabular-nums ${c.count > 0 ? "text-velvet-gold" : "text-white/25"}`}
                              >
                                {c.count}
                              </span>
                            </Link>
                            {c.towns && c.towns.length > 0 && (
                              <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
                                {c.towns.slice(0, 12).map((t) => (
                                  <li key={t.name}>
                                    <Link
                                      to={`/directory?city=${c.slug}&town=${encodeURIComponent(t.name)}`}
                                      data-testid={`cities-town-link-${c.slug}-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                                      className="text-xs font-light text-white/50 transition-colors hover:text-velvet-rose"
                                    >
                                      {t.name}
                                      {t.count > 0 && (
                                        <span className="ml-1 text-velvet-gold/70">({t.count})</span>
                                      )}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="py-20 text-center text-sm font-light text-white/40" data-testid="cities-empty">
                No cities match &ldquo;{query}&rdquo;.
              </div>
            )}
          </div>
        )}

        <div className="h-32" />
      </div>
    </div>
  );
}
